import React, { memo } from 'react';
import { Stack, Typography, CardContent, Card } from 'investira.react.components';
import { ATIVO_MERCADO } from '@investirapri/financa.js/lib/enums';
import { FUNDO_SITUACAO } from '../../../enums';
import { InlineInfo } from '../../../components/molecules';
import { formats, validators } from 'investira.sdk';

/**
 * Localiza a situação pelo valor persistido no fundo.
 *
 * @param {string} pSituacao - Valor da situação recebido do backend.
 * @returns {Object|null} Configuração correspondente no catálogo local.
 */
const pvFundoSituacao = pSituacao => {
  return Object.values(FUNDO_SITUACAO).find(pItem => pItem.value === pSituacao) || null;
};

/**
 * Formata um valor percentual preservando o valor zero.
 *
 * @param {number|string} pValue Valor percentual.
 * @returns {string}
 */
function pvFormatPercentual(pValue) {
  return `${formats.formatNumber(pValue, 2, true, false)}%`;
}

const AtivoInformacoes = memo(props => {
  const { ativo, variant = 'default', title = 'Informações do Ativo' } = props;
  const xFundoSituacao = pvFundoSituacao(ativo.fundo?.situacao);

  function fundoValue(pFundo, pKey) {
    if (validators.isEmpty(pFundo)) {
      return null;
    }

    if (!pFundo.hasOwnProperty(pKey)) {
      return null;
    }

    if (validators.isEmpty(pFundo[pKey])) {
      return null;
    }

    if (['fechado', 'condominio_fechado'].includes(pKey)) {
      return pFundo[pKey] ? 'Fechado' : 'Aberto';
    }

    if (['exclusivo', 'esg', 'cotas', 'estruturado'].includes(pKey)) {
      return pFundo[pKey] ? 'Sim' : 'Não';
    }

    if (pKey === 'data_inicio' && validators.isTrue(pFundo?.cvm_175)) {
      return pFundo.data_inicio;
    }

    if (pKey === 'taxas') {
      if (validators.isEmpty(pFundo[pKey].adm)) {
        return null;
      }

      return pvFormatPercentual(pFundo[pKey].adm);
    }

    if (pKey === 'performance') {
      if (validators.isEmpty(pFundo[pKey].taxa)) {
        return null;
      }

      return pvFormatPercentual(pFundo[pKey].taxa);
    }

    return pFundo[pKey];
  }

  const pvRenderContent = pTitle => {
    return (
      <Stack
        spacing={2}
        sx={{
          py: 2
        }}>
        <Stack direction="row">
          <Stack>
            <Typography
              variant="subtitle1"
              color="textPrimary"
              sx={{
                fontWeight: 600
              }}>
              {pTitle}
            </Typography>
          </Stack>
          <Stack flexGrow="1"></Stack>
        </Stack>
        <Stack
          direction="column"
          sx={{
            gap: 1
          }}>
          <InlineInfo label="Ativo" value={ativo.ativo} />
          <InlineInfo label="ISIN" value={ativo.isin} />
          <InlineInfo label="Ticker" value={ativo.ticker} />
          <InlineInfo label="Tipo" value={fundoValue(ativo.fundo, 'tipo')} />
          <InlineInfo label="Emissor" value={ativo.emissor?.pessoa} />
          <InlineInfo label="Mercado" value={ATIVO_MERCADO[ativo.mercado].descricao} />

          {/* TODO: testar ativo de renda fixa */}
          <InlineInfo label="Série" value={ativo.serie} />
          <InlineInfo label="Curva" value={ativo.curva_id} />
          {/* <InlineInfo
        label="Juros"
        value={ativo?.titulo?.evento?.juros?.periodicidade}
       /> */}

          {/* <InlineInfo label="Espécie" value={ativo?.acao?.especie} /> */}

          {/* TODO: testar ativo de renda fixa */}
          <InlineInfo label="Emissão" value={ativo.emissao} variant="date" />
          <InlineInfo label="Vencimento" value={ativo.vencimento} variant="date" />
          <InlineInfo
            label="Situação"
            value={xFundoSituacao?.descricao || fundoValue(ativo.fundo, 'situacao')}
          />
          <InlineInfo
            label="Início de Funcionamento"
            value={fundoValue(ativo.fundo, 'data_situacao')}
            variant="date"
          />
          <InlineInfo
            label="Adaptação à Resolução CVM 175"
            value={fundoValue(ativo.fundo, 'data_inicio')}
            variant="date"
          />
          <InlineInfo label="Fundo" value={fundoValue(ativo.fundo, 'fechado')} />
          <InlineInfo label="Exclusivo" value={fundoValue(ativo.fundo, 'exclusivo')} />
          <InlineInfo label="ESG" value={fundoValue(ativo.fundo, 'esg')} />
          <InlineInfo label="Fundo de Cotas" value={fundoValue(ativo.fundo, 'cotas')} />
          <InlineInfo label="Estruturado" value={fundoValue(ativo.fundo, 'estruturado')} />
          <InlineInfo
            label="Classificação Anbima"
            value={fundoValue(ativo.fundo, 'classificacao_anbima')}
          />
          <InlineInfo label="Benchmark" value={fundoValue(ativo.fundo, 'desempenho')} />
          <InlineInfo label="Taxa de Administração" value={fundoValue(ativo.fundo, 'taxas')} />
          <InlineInfo label="Taxa de Performance" value={fundoValue(ativo.fundo, 'performance')} />
          <InlineInfo label="Condomínio" value={fundoValue(ativo.fundo, 'condominio_fechado')} />
        </Stack>
      </Stack>
    );
  };

  if (variant === 'onlyContent') {
    return pvRenderContent();
  }

  return (
    <Stack flexGrow={1}>
      <Card>
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
          {pvRenderContent(title)}
        </CardContent>
      </Card>
    </Stack>
  );
});
AtivoInformacoes.displayName = 'AtivoInformacoes';
export default AtivoInformacoes;
